// 晌木时光 · 工作台看板 — 生产版(读取共享配置中心 · 浅色默认 · 实时搜索 · 置顶收藏)
import React, { useEffect as uE, useId, useMemo as uM, useRef as uR, useState as uS } from 'react';
import {
  Brand,
  cssVars,
  fmtDate,
  fmtTime,
  Icon,
  ItemIcon,
  isDarkTheme,
  nextThemeMode,
  normalizeThemeMode,
  theme,
  useClock,
  useConfig,
  welcomeGreeting,
} from '../shared/nav-shared.jsx';
import { EDIT_PINS_KEY, MuyuanStore, clone, configErrorMessage, getToken } from '../store/api-store.js';

import { useDialog } from '../shared/use-dialog.js';
import { moveBy, OrderControls } from '../shared/order-controls.jsx';

const WEATHER_CACHE_KEY = 'muyuan_weather_cache';
const WEATHER_MAX_AGE = 30 * 60 * 1000;

function weatherFallback(cfg) {
  const site = cfg.site || {};
  const weather = cfg.weather || {};
  const tempNow = Number(weather.tempNow ?? weather.tempHigh ?? 0);
  // 后台配置只作为接口失败时的兜底；实时数据回来后会覆盖这些值。
  return {
    enabled: weather.enabled !== false,
    location: site.location || weather.location || '',
    cond: weather.cond || '多云',
    icon: weather.icon || (weather.cond === '晴' ? '100' : ''),
    tempNow,
    tempHigh: Number(weather.tempHigh ?? tempNow),
    tempLow: Number(weather.tempLow ?? tempNow),
    humidity: weather.humidity == null ? null : Number(weather.humidity),
    todayText: weather.todayText || weather.cond || '多云',
    todayTextDay: weather.todayTextDay || weather.cond || '多云',
    todayTextNight: weather.todayTextNight || weather.cond || '多云',
    todayIconDay: weather.todayIconDay || '',
    todayIconNight: weather.todayIconNight || '',
  };
}

function weatherPlaceholder(cfg) {
  const fallback = weatherFallback(cfg);
  return {
    ...fallback,
    cond: '',
    tempHigh: null,
    tempLow: null,
    tempNow: null,
    humidity: null,
    todayText: '',
    todayTextDay: '',
    todayTextNight: '',
    loading: fallback.enabled,
  };
}

function readWeatherCache(cfg) {
  const fallback = weatherFallback(cfg);
  if (!fallback.enabled || !fallback.location) return null;
  try {
    const cache = JSON.parse(localStorage.getItem(WEATHER_CACHE_KEY) || '{}');
    const cached = cache[fallback.location];
    if (!cached) return null;
    if (cached.tempNow == null || cached.humidity == null) return null;
    // 旧版缓存没有图标和今日摘要，继续使用会导致刷新时短暂回到旧天气样式。
    if (cached.icon == null || cached.todayText == null) return null;
    return { ...fallback, ...cached, location: fallback.location, enabled: true, loading: false, stale: !cached.savedAt || Date.now() - cached.savedAt > WEATHER_MAX_AGE };
  } catch {
    localStorage.removeItem(WEATHER_CACHE_KEY);
    return null;
  }
}

function saveWeatherCache(location, weather) {
  if (!location) return;
  try {
    const cache = JSON.parse(localStorage.getItem(WEATHER_CACHE_KEY) || '{}');
    cache[location] = {
      cond: weather.cond,
      icon: weather.icon,
      tempNow: weather.tempNow,
      tempHigh: weather.tempHigh,
      tempLow: weather.tempLow,
      humidity: weather.humidity,
      todayText: weather.todayText,
      todayTextDay: weather.todayTextDay,
      todayTextNight: weather.todayTextNight,
      todayIconDay: weather.todayIconDay,
      todayIconNight: weather.todayIconNight,
      savedAt: Date.now(),
    };
    localStorage.setItem(WEATHER_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Weather is decorative; a failed local cache should not affect navigation.
  }
}

function initialWeather(cfg) {
  const fallback = weatherFallback(cfg);
  if (!fallback.enabled) return fallback;
  return readWeatherCache(cfg) || weatherPlaceholder(cfg);
}

function weatherKind(weather = {}) {
  // 图标以和风实时 icon 为准：例如 104=阴，305=小雨；文字只做兜底判断。
  const code = String(weather.icon || '').trim();
  const text = String(weather.cond || '').trim();
  if (/^3/.test(code) || text.includes('雨')) return 'rain';
  if (/^4/.test(code) || text.includes('雪')) return 'snow';
  if (/^5/.test(code) || /(雾|霾|沙|尘)/.test(text)) return 'fog';
  if (code === '100' || code === '900' || text === '晴') return 'sun';
  if (code === '150') return 'moon';
  return 'cloud';
}

function WeatherGlyph({ kind, s = 18 }) {
  if (kind === 'sun') return <Icon.sun s={s} />;
  if (kind === 'moon') return <Icon.moon s={s} />;
  if (kind === 'rain') {
    return (
      <svg viewBox="0 0 24 24" width={s} height={s} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 15h9a4 4 0 0 0 .5-7.97A5.5 5.5 0 0 0 6 8.5 3.5 3.5 0 0 0 7 15Z" />
        <path d="M8 19v1.5M12 18.5V21M16 19v1.5" />
      </svg>
    );
  }
  if (kind === 'snow') {
    return (
      <svg viewBox="0 0 24 24" width={s} height={s} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 15h9a4 4 0 0 0 .5-7.97A5.5 5.5 0 0 0 6 8.5 3.5 3.5 0 0 0 7 15Z" />
        <path d="M9 20h.01M13 19h.01M17 20h.01" />
      </svg>
    );
  }
  if (kind === 'fog') {
    return (
      <svg viewBox="0 0 24 24" width={s} height={s} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 13h9a4 4 0 0 0 .5-7.97A5.5 5.5 0 0 0 6 6.5 3.5 3.5 0 0 0 7 13Z" />
        <path d="M5 17h14M7 20h10" />
      </svg>
    );
  }
  return <Icon.cloud s={s} />;
}

function WeatherIcon({ weather, s = 18, className = '' }) {
  const kind = weatherKind(weather);
  return (
    <span
      className={'ws-weather-icon' + (className ? ` ${className}` : '')}
      data-weather-kind={kind}
      data-weather-code={weather.icon || ''}
      title={weather.cond || weather.todayText || ''}
    >
      <WeatherGlyph kind={kind} s={s} />
    </span>
  );
}

function weatherSummaryLine(weather = {}) {
  const cond = String(weather.cond || '').trim();
  const today = String(weather.todayText || '').trim();
  if (cond && today && today !== cond) return `${cond} · 今日${today}`;
  if (cond && today) return `${cond} · 今日天气平稳`;
  return cond || today || '天气更新中';
}

/* ── personal-ordering helpers (apply each employee's saved order over the global data) ── */
function reorderIds(ids, fromId, toId) {
  const a = ids.filter((x) => x !== fromId);
  const idx = a.indexOf(toId);
  if (idx < 0) a.push(fromId); else a.splice(idx, 0, fromId);
  return a;
}

function reorderPinnedIds(ids, fromId, toId) {
  const fromIndex = ids.indexOf(fromId);
  const toIndex = ids.indexOf(toId);
  if (fromIndex < 0 || toIndex < 0 || fromId === toId) return ids.slice();
  const next = ids.filter((x) => x !== fromId);
  const targetIndex = next.indexOf(toId);
  const insertAt = fromIndex < toIndex ? targetIndex + 1 : targetIndex;
  next.splice(insertAt, 0, fromId);
  return next;
}
function applyOrder(groups, prefs) {
  // Personal order is an overlay only: missing ids are ignored and new admin-added ids fall back to global order.
  const catOrder = (prefs && prefs.catOrder) || [];
  const itemOrder = (prefs && prefs.itemOrder) || {};
  const gmap = {}; groups.forEach((g) => { gmap[g.id] = g; });
  const og = [];
  catOrder.forEach((id) => { if (gmap[id]) { og.push(gmap[id]); delete gmap[id]; } });
  groups.forEach((g) => { if (gmap[g.id]) og.push(g); });
  return og.map((g) => {
    const io = itemOrder[g.id] || [];
    const imap = {}; g.items.forEach((it) => { imap[it.id] = it; });
    const its = [];
    io.forEach((id) => { if (imap[id]) { its.push(imap[id]); delete imap[id]; } });
    g.items.forEach((it) => { if (imap[it.id]) its.push(it); });
    return Object.assign({}, g, { items: its });
  });
}

/* ── external web search engines ── */
const ENGINES = [
  { id: 'baidu',  name: '百度',     url: 'https://www.baidu.com/s?wd=',            ph: '百度一下，你就知道' },
  { id: 'google', name: 'Google',  url: 'https://www.google.com/search?q=',       ph: 'Search Google' },
  { id: 'bing',   name: '必应',     url: 'https://www.bing.com/search?q=',         ph: '必应搜索' },
  { id: 'taobao', name: '淘宝',     url: 'https://s.taobao.com/search?q=',         ph: '搜索淘宝商品' },
  { id: 'jd',     name: '京东',     url: 'https://search.jd.com/Search?keyword=',  ph: '搜索京东商品' },
];

function WebSearchBar() {
  const [eid, setEid] = uS(() => { try { return localStorage.getItem('muyuan.engine') || 'baidu'; } catch (e) { return 'baidu'; } });
  const [q, setQ] = uS('');
  const [open, setOpen] = uS(false);
  const wrapRef = uR(null);
  const eng = ENGINES.find((e) => e.id === eid) || ENGINES[0];

  const pick = (id) => { setEid(id); setOpen(false); try { localStorage.setItem('muyuan.engine', id); } catch (e) {} };
  const go = (e) => {
    e.preventDefault();
    const t = q.trim();
    if (!t) return;
    window.open(eng.url + encodeURIComponent(t), '_blank', 'noopener');
    setQ('');
  };

  uE(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener('mousedown', h);
    return () => window.removeEventListener('mousedown', h);
  }, []);

  return (
    <form ref={wrapRef} onSubmit={go} className="ws-search" style={{ height: 46, padding: '0 6px 0 4px', position: 'relative' }}>
      {/* engine selector */}
      <button type="button" onClick={() => setOpen((v) => !v)} className="ws-engbtn" title="选择搜索引擎" aria-expanded={open}>
        <span style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--ws-text)' }}>{eng.name}</span>
        <Icon.chevron s={14} />
      </button>
      <span style={{ width: 1, height: 22, background: 'var(--ws-border)', flex: '0 0 auto' }} />
      {open && (
        <div className="ws-engmenu">
          {ENGINES.map((e) => (
            <button type="button" key={e.id} onClick={() => pick(e.id)} className={'ws-engopt' + (e.id === eid ? ' on' : '')}>
              <span>{e.name}</span>
              {e.id === eid && <Icon.check s={14} />}
            </button>
          ))}
        </div>
      )}
      <input aria-label="搜索互联网" value={q} onChange={(ev) => setQ(ev.target.value)} placeholder={eng.ph} style={{ paddingLeft: 4 }} />
      {q && <button type="button" className="ws-clearbtn" onClick={() => setQ('')} title="清除"><Icon.close s={16} /></button>}
      <button type="submit" className="ws-searchbtn" title="搜索"><Icon.search s={17} /><span>搜索</span></button>
    </form>
  );
}

/* ── pin control — in edit mode shows a clear +/− button pinned to the right; otherwise a static star (hidden when not pinned) ── */
function PinStar({ item, pinned, onPin, edit, s }) {
  if (edit) {
    return (
      <button
        className={'ws-pinbtn' + (pinned ? ' remove' : ' add')}
        title={pinned ? '从常用移除' : '加入常用'}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onPin(item.id); }}
      >
        {pinned ? <Icon.minus s={15} /> : <Icon.plus s={15} />}
      </button>
    );
  }
  if (!pinned) return null;
  return (
    <span className="ws-star on" style={{ pointerEvents: 'none' }} title="已置顶">
      <Icon.starFill s={s} />
    </span>
  );
}

/* ── chip with click-to-open + pin star (draggable in organize mode) ── */
function PChip({ item, pinned, onPin, dark, edit, organize, drag, gid, onMoveUp, onMoveDown }) {
  const Link = organize || edit ? 'span' : 'a';
  const dragging = organize && drag && drag.over === 'item:' + item.id;
  return (
    <div
      className={'ws-chip' + (organize ? ' ws-org' : '') + (dragging ? ' ws-dragover' : '')}
      data-item-id={item.id}
      data-group-id={gid}
      style={{ cursor: organize ? 'grab' : 'pointer', paddingRight: 9 }}
      title={organize ? '拖动排序：' + item.name : item.name + ' · ' + item.url}
      draggable={organize || undefined}
      onDragStart={organize ? (e) => drag.startItem(e, item.id, gid) : undefined}
      onDragOver={organize ? (e) => drag.overItem(e, item.id, gid) : undefined}
      onDrop={organize ? (e) => drag.dropItem(e, item.id, gid) : undefined}
      onDragEnd={organize ? drag.end : undefined}
    >
      {organize && <span className="ws-griphandle"><Icon.grip s={15} /></span>}
      <Link className="ws-entry-link" href={Link === 'a' ? item.url : undefined} target={Link === 'a' && item.newTab !== false ? '_blank' : undefined} rel={Link === 'a' ? 'noopener noreferrer' : undefined}>
        <ItemIcon item={item} size={38} radius={11} dark={dark} />
        <span className="ws-entry-name">{item.name}</span>
      </Link>
      {organize && <OrderControls label={item.name} onUp={onMoveUp} onDown={onMoveDown} />}
      {!organize && edit && <PinStar item={item} pinned={pinned} onPin={onPin} edit={edit} s={13} />}
    </div>
  );
}

/* ── larger clickable card (pinned + search results) ── */
function PCard({ item, sub, pinned, onPin, dark, big, edit, pinDrag, onMoveUp, onMoveDown }) {
  const Link = edit ? 'span' : 'a';
  const dragging = pinDrag && pinDrag.dragging === item.id;
  const over = pinDrag && pinDrag.over === item.id && !dragging;
  return (
    <div
      className={'ws-card ws-procard' + (pinDrag ? ' ws-pin-draggable' : '') + (dragging ? ' dragging' : '') + (over ? ' over' : '')}
      data-pin-id={item.id}
      title={(pinDrag ? '拖动调整置顶顺序 · ' : '') + item.name + ' · ' + item.url}
      draggable={pinDrag ? true : undefined}
      onDragStart={pinDrag ? (e) => pinDrag.start(e, item.id) : undefined}
      onDragEnter={pinDrag ? (e) => pinDrag.enter(e, item.id) : undefined}
      onDragOver={pinDrag ? (e) => pinDrag.overCard(e, item.id) : undefined}
      onDrop={pinDrag ? (e) => pinDrag.drop(e, item.id) : undefined}
      onDragEnd={pinDrag ? pinDrag.end : undefined}
      onMouseDown={pinDrag ? (e) => pinDrag.mouseStart(e, item.id) : undefined}
      onMouseEnter={pinDrag ? (e) => pinDrag.mouseEnter(e, item.id) : undefined}
      onMouseOver={pinDrag ? (e) => pinDrag.mouseEnter(e, item.id) : undefined}
      onMouseUp={pinDrag ? (e) => pinDrag.mouseUp(e, item.id) : undefined}
    >
      {pinDrag && <span className="ws-griphandle" title="拖动调整置顶顺序"><Icon.grip s={15} /></span>}
      <Link className="ws-entry-link" href={Link === 'a' ? item.url : undefined} target={Link === 'a' && item.newTab !== false ? '_blank' : undefined} rel={Link === 'a' ? 'noopener noreferrer' : undefined}>
        <ItemIcon item={item} size={big ? 40 : 38} radius={12} dark={dark} />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span className="ws-linkname">{item.name}</span>
          <span className="ws-linknote">{sub}</span>
        </span>
      </Link>
      {edit && <OrderControls label={item.name} onUp={onMoveUp} onDown={onMoveDown} />}
      <PinStar item={item} pinned={pinned} onPin={onPin} edit={edit} s={15} />
    </div>
  );
}

function CatBento({ g, pins, onPin, dark, edit, organize, drag, onMoveUp, onMoveDown, moveItem }) {
  if (!g.items.length && !organize) return null;
  const dragging = organize && drag && (drag.over === 'cat:' + g.id || drag.over === 'catdrop:' + g.id);
  return (
    <div
      className={'ws-bento' + (organize ? ' ws-org' : '') + (dragging ? ' ws-dragover-cat' : '')}
      data-group-id={g.id}
      style={{ breakInside: 'avoid', marginBottom: 16 }}
      onDragOver={organize ? (e) => drag.overCat(e, g.id) : undefined}
      onDrop={organize ? (e) => drag.dropCat(e, g.id) : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
        {organize && (
          <span
            className="ws-cathandle"
            title="拖动整理分类顺序"
            draggable
            onDragStart={(e) => drag.startCat(e, g.id)}
            onDragEnd={drag.end}
          ><Icon.grip s={17} /></span>
        )}
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, letterSpacing: '-0.005em', color: 'var(--ws-text)' }}>{g.name}</h3>
        <span className="ws-seccount">{g.items.length}</span>
        {organize && <OrderControls label={`分类 ${g.name}`} onUp={onMoveUp} onDown={onMoveDown} />}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ws-text3)' }}>{g.desc}</span>
      </div>
      <div className="ws-chipgrid">
        {g.items.map((it, index) => <PChip key={it.id} item={it} gid={g.id} pinned={pins.includes(it.id)} onPin={onPin} dark={dark} edit={edit} organize={organize} drag={drag} onMoveUp={index > 0 ? () => moveItem(g.id, it.id, -1) : null} onMoveDown={index < g.items.length - 1 ? () => moveItem(g.id, it.id, 1) : null} />)}
      </div>
    </div>
  );
}

function NavigationCatalog({ groups, organize, moveCategory, ...props }) {
  const [query, setQuery] = uS('');
  const keyword = query.trim().normalize('NFKC').toLowerCase();
  const matches = (group, item) => [group.name, item.name, item.note, item.url].join(' ').normalize('NFKC').toLowerCase().includes(keyword);
  const filtered = keyword && !organize ? groups.map((group) => ({ ...group, items: group.items.filter((item) => matches(group, item)) })).filter((group) => group.items.length) : groups;
  return <>
    <div className="ws-nav-search">
      <Icon.search s={17} />
      <input aria-label="搜索站内入口" placeholder="搜索入口名称、分类、备注或网址" value={query} disabled={organize} onChange={(event) => setQuery(event.target.value)} />
      {query && <button type="button" aria-label="清除站内搜索" onClick={() => setQuery('')}>清除</button>}
    </div>
    {keyword && !organize && <p role="status" className="ws-search-summary">{filtered.length ? `找到 ${filtered.reduce((sum, group) => sum + group.items.length, 0)} 个入口` : '没有找到匹配入口，请换个关键词。'}</p>}
    <div className="ws-catgrid">
      {filtered.map((group, index) => <CatBento key={group.id} g={group} organize={organize} {...props} onMoveUp={index > 0 ? () => moveCategory(group.id, -1) : null} onMoveDown={index < groups.length - 1 ? () => moveCategory(group.id, 1) : null} />)}
    </div>
  </>;
}

function Widget({ children, className = '', style, blockId, blockTitle, blockDrag }) {
  const editing = Boolean(blockId && blockDrag);
  const isOver = editing && blockDrag.overId === blockId;
  return (
    <div
      className={'ws-bento' + (className ? ` ${className}` : '') + (editing ? ' ws-layout-edit-block' : '') + (isOver ? ' over' : '')}
      data-block-id={blockId || undefined}
      style={style}
      onDragEnter={editing ? () => blockDrag.enter(blockId) : undefined}
      onDragOver={editing ? (e) => blockDrag.overBlock(e, blockId) : undefined}
      onDrop={editing ? (e) => blockDrag.drop(e, blockId) : undefined}
    >
      {editing && (
        <div className="ws-layout-block-handle" draggable
          onDragStart={(e) => blockDrag.start(e, blockId)}
          onDragEnd={blockDrag.end}
          title="拖拽排序模块">
          <Icon.grip s={14} />
          <span>{blockTitle}</span>
          {blockDrag.controls(blockId, blockTitle)}
        </div>
      )}
      {children}
    </div>
  );
}

function canEditPromotion(user) {
  return Boolean(user && (user.role === 'admin' || user.canEditPromotion === true));
}

function PromotionWidget({ cfg, user, style, blockId, blockTitle, blockDrag, disableEditing = false }) {
  const text = cfg.promotion?.text || '';
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const [editing, setEditing] = uS(false);
  const [draft, setDraft] = uS(text);
  const [saving, setSaving] = uS(false);
  const [err, setErr] = uS('');
  const [conflict, setConflict] = uS(false);
  const draftRevision = uR(cfg.revision);
  const editable = !disableEditing && canEditPromotion(user);

  uE(() => {
    if (!editing) setDraft(text);
  }, [text, editing]);

  const save = async () => {
    if (saving || !editable) return;
    setSaving(true);
    setErr('');
    try {
      await MuyuanStore.savePromotionText(draft, draftRevision.current);
      setEditing(false);
    } catch (error) {
      setConflict(error.status === 409);
      setErr(error.status === 409 ? '内容已被更新，当前草稿已保留。请保留需要的文字，再重新载入后编辑。' : configErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const toggleEditing = () => {
    if (!editing) { draftRevision.current = cfg.revision; setDraft(text); }
    setEditing(!editing);
    setErr('');
    setConflict(false);
  };
  const reload = async () => {
    if (!window.confirm('重新载入将放弃当前大促草稿，是否继续？')) return;
    setSaving(true);
    try {
      const latest = await MuyuanStore.fetchConfig();
      draftRevision.current = latest.revision;
      setDraft(latest.promotion?.text || '');
      setErr('');
      setConflict(false);
    } catch (error) { setErr(configErrorMessage(error)); }
    finally { setSaving(false); }
  };

  return (
    <Widget className="ws-promo-card" style={{ position: 'relative', ...style }} blockId={blockId} blockTitle={blockTitle} blockDrag={blockDrag}>
      <div className="ws-promo-head">
        <div>
          <h3>平台大促</h3>
        </div>
        {editing && (
          <div className="ws-promo-actions">
            <span>{draft.trim().length}/240</span>
            <button type="button" disabled={saving} onClick={() => { setDraft(text); setEditing(false); setErr(''); }}>取消</button>
            <button type="button" className="primary" disabled={saving} onClick={save}>{saving ? '保存中...' : '保存'}</button>
          </div>
        )}
        {editable && (
          <button disabled={saving} className="ws-promo-edit" onClick={toggleEditing} title={editing ? '收起编辑' : '编辑大促内容'}>
            <Icon.gear s={13} />
          </button>
        )}
      </div>
      {editing && err && <div className="ws-promo-notice" role="alert">{err}{conflict && <button type="button" disabled={saving} onClick={reload}>重新载入</button>}</div>}
      <div className="ws-promo-scroll">
        {editing ? (
          <div className="ws-promo-editor">
            <textarea aria-label="平台大促内容" disabled={saving} value={draft} maxLength={240} onChange={(e) => setDraft(e.target.value)} placeholder="填写平台大促活动、时间节点、注意事项..." />
          </div>
        ) : (
          <div className="ws-promo-body">
            {lines.length ? (
              <React.Fragment>
                <div className="ws-promo-title">{lines[0]}</div>
                <div className="ws-promo-lines">
                  {lines.slice(1).map((line, index) => <div key={index}>{line}</div>)}
                </div>
              </React.Fragment>
            ) : (
              <div className="ws-promo-empty">暂无平台大促信息</div>
            )}
          </div>
        )}
      </div>
    </Widget>
  );
}

/* ── account login modal ── */
function LoginModal({ onClose, onLogin }) {
  const [username, setUsername] = uS('');
  const [password, setPassword] = uS('');
  const [err, setErr] = uS('');
  const [loading, setLoading] = uS(false);
  const ref = uR(null);
  const dialogRef = useDialog(onClose);
  const dialogId = useId();
  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const account = username.trim().toLowerCase();
    if (!account || !password) { setErr('请输入账号和密码'); return; }
    setLoading(true);
    try {
      const user = await MuyuanStore.login(account, password);
      onLogin(user);
    } catch (error) {
      setErr(error.status === 401 ? '账号或密码不正确' : (error.message || '暂时无法登录，请稍后重试'));
      setPassword('');
      setLoading(false);
    }
  };
  return (
    <div className="ws-modal-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form ref={dialogRef} className="ws-modal" role="dialog" aria-modal="true" aria-labelledby={dialogId} tabIndex={-1} onSubmit={submit}>
        <button type="button" className="ws-modal-x" onClick={onClose} title="关闭"><Icon.close s={18} /></button>
        <div className="ws-modal-ico"><Icon.user s={26} /></div>
        <h2 id={dialogId} style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800 }}>员工登录</h2>
        <p style={{ margin: '0 0 20px', fontSize: 13.5, color: 'var(--ws-text3)', lineHeight: 1.5 }}>使用公司账号登录。员工可同步个人常用和排序，具有管理权限的账号可进入后台。</p>
        <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--ws-text2)', marginBottom: 6 }} htmlFor={`${dialogId}-username`}>账号</label>
        <input id={`${dialogId}-username`} ref={ref} className="ws-modal-input" value={username} placeholder="例如: zhangsan" autoComplete="username" onChange={(e) => { setUsername(e.target.value); setErr(''); }} />
        <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--ws-text2)', margin: '12px 0 6px' }} htmlFor={`${dialogId}-password`}>密码</label>
        <input id={`${dialogId}-password`} className="ws-modal-input" type="password" value={password} placeholder="请输入密码" autoComplete="current-password" onChange={(e) => { setPassword(e.target.value); setErr(''); }} />
        {err && <div style={{ color: '#c2462f', fontSize: 12.5, marginTop: 7 }}>{err}</div>}
        <button type="submit" className="ws-modal-go" disabled={loading}>{loading ? '登录中...' : '登录'}</button>
        <a href="/admin" className="ws-modal-admin">进入后台管理 →</a>
      </form>
    </div>
  );
}

function ChangePasswordModal({ onClose }) {
  const dialogRef = useDialog(onClose);
  const dialogId = useId();
  const [currentPassword, setCurrentPassword] = uS('');
  const [newPassword, setNewPassword] = uS('');
  const [confirmPassword, setConfirmPassword] = uS('');
  const [err, setErr] = uS('');
  const [loading, setLoading] = uS(false);
  const submit = async (event) => {
    event.preventDefault();
    if (loading) return;
    setErr('');
    if (!currentPassword || !newPassword) { setErr('请填写当前密码和新密码'); return; }
    if (newPassword.length < 6) { setErr('新密码至少 6 位'); return; }
    if (newPassword !== confirmPassword) { setErr('两次输入的新密码不一致'); return; }
    setLoading(true);
    try {
      await MuyuanStore.changePassword(currentPassword, newPassword);
      onClose('密码已修改');
    } catch (error) {
      setErr(error.status === 400 ? '当前密码不正确，或新密码不符合要求' : '密码修改失败，请稍后重试');
      setLoading(false);
    }
  };
  return (
    <div className="ws-modal-back" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <form ref={dialogRef} className="ws-modal" role="dialog" aria-modal="true" aria-labelledby={dialogId} tabIndex={-1} onSubmit={submit}>
        <button type="button" className="ws-modal-x" onClick={() => onClose()} title="关闭"><Icon.close s={18} /></button>
        <div className="ws-modal-ico"><Icon.gear s={26} /></div>
        <h2 id={dialogId} style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800 }}>修改登录密码</h2>
        <p style={{ margin: '0 0 20px', fontSize: 13.5, color: 'var(--ws-text3)', lineHeight: 1.5 }}>修改成功后会自动更新当前登录，其他设备上的旧会话将失效。</p>
        <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--ws-text2)', marginBottom: 6 }} htmlFor={`${dialogId}-current`}> 当前密码</label>
        <input id={`${dialogId}-current`} className="ws-modal-input" type="password" value={currentPassword} autoComplete="current-password" onChange={(event) => setCurrentPassword(event.target.value)} />
        <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--ws-text2)', margin: '12px 0 6px' }} htmlFor={`${dialogId}-new`}> 新密码</label>
        <input id={`${dialogId}-new`} className="ws-modal-input" type="password" value={newPassword} autoComplete="new-password" placeholder="至少 6 位" onChange={(event) => setNewPassword(event.target.value)} />
        <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--ws-text2)', margin: '12px 0 6px' }} htmlFor={`${dialogId}-confirm`}> 确认新密码</label>
        <input id={`${dialogId}-confirm`} className="ws-modal-input" type="password" value={confirmPassword} autoComplete="new-password" onChange={(event) => setConfirmPassword(event.target.value)} />
        {err && <div style={{ color: '#c2462f', fontSize: 12.5, marginTop: 7 }}>{err}</div>}
        <button type="submit" className="ws-modal-go" disabled={loading}>{loading ? '修改中...' : '确认修改'}</button>
      </form>
    </div>
  );
}

/* ── account chip + dropdown (when logged in) ── */
function AccountMenu({ user, onLogout }) {
  const [open, setOpen] = uS(false);
  const [changingPassword, setChangingPassword] = uS(false);
  const [notice, setNotice] = uS('');
  const ref = uR(null);
  uE(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener('mousedown', h);
    return () => window.removeEventListener('mousedown', h);
  }, []);
  const initial = (user.name || user.username || '?').slice(0, 1).toUpperCase();
  const adminAccess = MuyuanStore.canAccessAdmin(user);
  const accountType = user.role === 'admin' ? '管理员' : (adminAccess ? '授权管理账号' : '普通账号');
  return (
    <div ref={ref} style={{ position: 'relative', flex: '0 0 auto' }}>
      <button className="ws-acct" onClick={() => setOpen((v) => !v)} title={user.username}>
        <span className="ws-acct-av">{initial}</span>
        <span style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name || user.username}</span>
        <Icon.chevron s={13} />
      </button>
      {open && (
        <div className="ws-acct-menu">
          <div style={{ padding: '4px 10px 10px', borderBottom: '1px solid var(--ws-border-soft)', marginBottom: 6 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800 }}>{user.name || user.username}</div>
            <div style={{ fontSize: 12, color: 'var(--ws-text3)', wordBreak: 'break-all' }}>{accountType} · {user.username}</div>
          </div>
          {adminAccess && <a href="/admin" className="ws-acct-item"><Icon.gear s={15} /><span>后台管理</span></a>}
          {user.authSource !== 'sso' && <button className="ws-acct-item" onClick={() => { setOpen(false); setChangingPassword(true); }}><Icon.gear s={15} /><span>修改密码</span></button>}
          <button className="ws-acct-item" onClick={() => { setOpen(false); onLogout(); }}><Icon.logout s={15} /><span>退出登录</span></button>
        </div>
      )}
      {notice && <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 49, whiteSpace: 'nowrap', padding: '8px 11px', borderRadius: 9, background: 'var(--ws-card)', border: '1px solid var(--ws-border)', color: 'var(--ws-text2)', fontSize: 12.5 }}>{notice}</div>}
      {changingPassword && <ChangePasswordModal onClose={(message) => { setChangingPassword(false); setNotice(message || ''); if (message) window.setTimeout(() => setNotice(''), 2400); }} />}
    </div>
  );
}

function ClockContent({ site, user, w }) {
  const now = useClock();
  return <>
                <div>
                  <div style={{ fontSize: 13, color: 'var(--ws-text3)', fontFamily: '"JetBrains Mono",monospace', letterSpacing: '0.05em' }}>{fmtDate(now)}</div>
                  <div style={{ fontFamily: '"JetBrains Mono",monospace', fontSize: 54, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.02, marginTop: 6 }}>{fmtTime(now)}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>{welcomeGreeting(now, site.team, user)}</div>
                </div>
                {w.enabled && (
                  <div className="ws-clock-weather" style={{ display: 'flex', alignItems: 'center', gap: 11, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--ws-border)' }}>
                    <WeatherIcon weather={w} s={30} className="ws-clock-weather-icon" />
                    <div className="ws-clock-weather-body">
                      {w.loading || w.unavailable ? (
                        <>
                          <div className="ws-clock-weather-loading">{w.unavailable ? '天气暂不可用' : '天气更新中'}</div>
                          <div className="ws-clock-weather-meta">{w.location}</div>
                        </>
                      ) : (
                        <>
                          {/* 两行布局：第一行当前天气+今日摘要，第二行地点+温度+湿度，避免卡片高度跳动。 */}
                          <div className="ws-clock-weather-live">
                            <span className="ws-clock-weather-temp-main">{w.tempNow ?? w.tempHigh}°</span>
                            <span className="ws-clock-weather-summary">{weatherSummaryLine(w)}{w.stale && <small className="ws-weather-stale">缓存天气 · 等待更新</small>}</span>
                          </div>
                          <div className="ws-clock-weather-meta">{w.location} · {w.tempHigh}° / {w.tempLow}° · 湿度 {w.humidity ?? '--'}%</div>
                        </>
                      )}
                    </div>
                  </div>
                )}
  </>;
}

function WorkstationApp({
  cfgOverride = null,
  updateOverride = null,
  configReadyOverride = null,
  layoutEditMode = false,
  onExitLayout,
  onSaveLayout,
  savingLayout = false,
} = {}) {
  const [loadedCfg, loadedUpdate, loadedReady, configState] = useConfig({ enabled: !cfgOverride });
  const cfg = cfgOverride || loadedCfg;
  const update = updateOverride || loadedUpdate;
  const configReady = configReadyOverride == null ? loadedReady : configReadyOverride;
  const [editPins, setEditPins] = uS(() => { try { return layoutEditMode || localStorage.getItem(EDIT_PINS_KEY) === '1'; } catch (e) { return layoutEditMode; } });
  const toggleEdit = () => {
    if (layoutEditMode) return;
    const next = !editPins;
    try { localStorage.setItem(EDIT_PINS_KEY, next ? '1' : '0'); } catch {}
    setEditPins(next);
  };

  // ── employee identity + personal layout prefs ──
  const [user, setUserState] = uS(() => MuyuanStore.getUser());
  const sessionToken = getToken();
  const userSession = uR(`${sessionToken}:${user?.username || ''}`);
  const [prefs, setPrefs] = uS(() => MuyuanStore.loadCachedPrefsFor());
  const prefsRef = uR(prefs);
  const prefsSaveId = uR(0);
  const [prefsStatus, setPrefsStatus] = uS({ saving: false, error: null });
  const [organize, setOrganize] = uS(() => layoutEditMode);
  const [showLogin, setShowLogin] = uS(false);
  const [themeMode, setThemeMode] = uS(() => {
    try { return localStorage.getItem('muyuan.theme') || null; } catch { return null; }
  });
  const [localDone, setLocalDone] = uS({});
  const [weatherNow, setWeatherNow] = uS(() => initialWeather(cfg));

  uE(() => {
    if (layoutEditMode) return undefined;
    let alive = true;
    const id = prefsSaveId.current;
    MuyuanStore.loadPrefsFor().then((next) => { if (alive) { prefsRef.current = next; setPrefs(next); } }).catch((error) => { if (alive && id === prefsSaveId.current && error.code !== 'session_changed') setPrefsStatus({ saving: false, error, operation: 'load' }); });
    return () => { alive = false; };
  }, [layoutEditMode, user && user.username, sessionToken]);
  uE(() => {
    if (layoutEditMode) return undefined;
    return MuyuanStore.subscribeUser((u) => {
      setUserState(u);
      const identity = `${getToken()}:${u?.username || ''}`;
      if (identity === userSession.current) return;
      userSession.current = identity;
      const next = MuyuanStore.loadCachedPrefsFor(u);
      prefsRef.current = next;
      setPrefs(next);
      prefsSaveId.current += 1;
      setPrefsStatus({ saving: false, error: null });
      if (!u) setOrganize(false);
    });
  }, [layoutEditMode]);
  uE(() => {
    if (layoutEditMode || !user) return undefined;
    let alive = true;
    MuyuanStore.verifySession().then((next) => { if (alive && next) setUserState(next); }).catch((error) => console.error(error));
    return () => { alive = false; };
  }, [layoutEditMode]);
  uE(() => {
    if (!layoutEditMode) return;
    setEditPins(true);
    setOrganize(true);
  }, [layoutEditMode]);

  const doLogin = (u) => { setUserState(u); setShowLogin(false); };
  const doLogout = () => { MuyuanStore.logout(); setOrganize(false); };
  const persistPrefs = (next) => {
    const id = ++prefsSaveId.current;
    setPrefsStatus({ saving: true, error: null });
    MuyuanStore.savePrefsFor(next).then(() => {
      if (id === prefsSaveId.current) setPrefsStatus({ saving: false, error: null });
    }).catch((error) => {
      if (id === prefsSaveId.current && error.code !== 'session_changed') setPrefsStatus({ saving: false, error, operation: 'save' });
    });
  };
  const retryPrefs = async () => {
    if (prefsStatus.operation !== 'load') { persistPrefs(prefsRef.current); return; }
    const id = ++prefsSaveId.current;
    setPrefsStatus({ saving: true, error: null, operation: 'load' });
    try {
      const next = await MuyuanStore.loadPrefsFor();
      if (id !== prefsSaveId.current) return;
      prefsRef.current = next;
      setPrefs(next);
      setPrefsStatus({ saving: false, error: null });
    } catch (error) {
      if (id === prefsSaveId.current && error.code !== 'session_changed') setPrefsStatus({ saving: false, error, operation: 'load' });
    }
  };
  const updatePrefs = (fn) => {
    const next = fn(clone(prefsRef.current));
    prefsRef.current = next;
    setPrefs(next);
    persistPrefs(next);
  };
  const resetOrder = () => { if (!layoutEditMode) updatePrefs((p) => ({ ...p, catOrder: [], itemOrder: {} })); };

  const themeModeCurrent = normalizeThemeMode(themeMode || cfg.theme);
  const dark = isDarkTheme(themeModeCurrent);
  const effectiveUser = user;
  const editPinsActive = layoutEditMode || editPins;
  const organizeActive = layoutEditMode || organize;
  const baseGroups = cfg.groups;
  const groups = uM(() => layoutEditMode ? baseGroups : applyOrder(baseGroups, prefs), [baseGroups, prefs, layoutEditMode]);
  const pins = layoutEditMode ? cfg.pins : (Array.isArray(prefs.pins) ? prefs.pins : cfg.pins);
  const site = cfg.site;
  const weatherConfig = cfg.weather || {};
  const w = weatherNow;

  uE(() => {
    const title = String(site?.name || '').trim();
    if (configReady && title) document.title = title;
  }, [configReady, site?.name]);

  uE(() => {
    if (!configReady) return undefined;
    const fallback = weatherFallback(cfg);
    if (!fallback.enabled) {
      setWeatherNow(fallback);
      return undefined;
    }
    let alive = true;
    let refreshing = false;
    setWeatherNow(readWeatherCache(cfg) || weatherPlaceholder(cfg));
    const refresh = async () => {
      if (refreshing) return;
      refreshing = true;
      try {
        const next = await MuyuanStore.fetchWeather(fallback.location);
        const merged = { ...fallback, ...next, enabled: true, location: fallback.location || next.location, loading: false, stale: false, savedAt: Date.now() };
        saveWeatherCache(merged.location, merged);
        if (alive) setWeatherNow(merged);
      } catch {
        const cached = readWeatherCache(cfg);
        if (alive) setWeatherNow(cached ? { ...cached, stale: true } : { ...weatherPlaceholder(cfg), loading: false, unavailable: true });
      } finally { refreshing = false; }
    };
    refresh();
    const timer = setInterval(refresh, 10 * 60 * 1000);
    const onVisible = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { alive = false; clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [
    configReady,
    site.location,
    weatherConfig.enabled,
    weatherConfig.cond,
    weatherConfig.icon,
    weatherConfig.tempHigh,
    weatherConfig.tempLow,
    weatherConfig.todayText,
  ]);

  // ── drag controller (HTML5 DnD · categories + chips) ──
  const dragRef = uR({ type: null, id: null, gid: null });
  const [dragOver, setDragOver] = uS(null);
  const reorderCat = (fromId, toId) => {
    if (layoutEditMode) {
      update((c) => {
        const order = reorderIds((c.groups || []).map((g) => g.id), fromId, toId);
        c.groups.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
        return c;
      });
      return;
    }
    updatePrefs((p) => { p.catOrder = reorderIds(groups.map((g) => g.id), fromId, toId); return p; });
  };
  const moveGlobalItem = (fromGid, toGid, fromId, toId = null) => {
    update((c) => {
      const source = (c.groups || []).find((g) => g.id === fromGid);
      const target = (c.groups || []).find((g) => g.id === toGid);
      if (!source || !target) return c;
      const sourceIndex = source.items.findIndex((it) => it.id === fromId);
      if (sourceIndex < 0) return c;
      const [item] = source.items.splice(sourceIndex, 1);
      const targetIndex = toId ? target.items.findIndex((it) => it.id === toId) : -1;
      const insertAt = targetIndex < 0 ? target.items.length : targetIndex;
      target.items.splice(insertAt, 0, item);
      return c;
    });
  };
  const reorderItem = (gid, fromId, toId, targetGid = gid) => {
    if (layoutEditMode) {
      moveGlobalItem(gid, targetGid, fromId, toId);
      return;
    }
    updatePrefs((p) => { const grp = groups.find((g) => g.id === gid); if (!grp) return p; p.itemOrder = Object.assign({}, p.itemOrder, { [gid]: reorderIds(grp.items.map((it) => it.id), fromId, toId) }); return p; });
  };
  const drag = {
    over: dragOver,
    startCat(e, id) { dragRef.current = { type: 'cat', id, gid: null }; e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', id); } catch (_) {} },
    startItem(e, id, gid) { e.stopPropagation(); dragRef.current = { type: 'item', id, gid }; e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', id); } catch (_) {} },
    overCat(e, id) {
      if (dragRef.current.type === 'cat') {
        e.preventDefault();
        if (dragOver !== 'cat:' + id) setDragOver('cat:' + id);
        return;
      }
      if (layoutEditMode && dragRef.current.type === 'item') {
        e.preventDefault();
        if (dragOver !== 'catdrop:' + id) setDragOver('catdrop:' + id);
      }
    },
    dropCat(e, id) {
      if (dragRef.current.type === 'cat') {
        e.preventDefault();
        if (dragRef.current.id !== id) reorderCat(dragRef.current.id, id);
      } else if (layoutEditMode && dragRef.current.type === 'item') {
        e.preventDefault();
        if (dragRef.current.gid !== id) moveGlobalItem(dragRef.current.gid, id, dragRef.current.id);
      }
      setDragOver(null); dragRef.current = { type: null };
    },
    overItem(e, id, gid) { if (dragRef.current.type !== 'item' || (!layoutEditMode && dragRef.current.gid !== gid)) return; e.stopPropagation(); e.preventDefault(); if (dragOver !== 'item:' + id) setDragOver('item:' + id); },
    dropItem(e, id, gid) { if (dragRef.current.type !== 'item' || (!layoutEditMode && dragRef.current.gid !== gid)) return; e.stopPropagation(); e.preventDefault(); if (dragRef.current.id !== id || dragRef.current.gid !== gid) reorderItem(dragRef.current.gid, dragRef.current.id, id, gid); setDragOver(null); dragRef.current = { type: null }; },
    end() { setDragOver(null); dragRef.current = { type: null }; },
  };
  const moveCategory = (id, delta) => {
    const next = moveBy(groups.map((g) => g.id), groups.findIndex((g) => g.id === id), delta);
    if (layoutEditMode) update((c) => { c.groups.sort((a, b) => next.indexOf(a.id) - next.indexOf(b.id)); return c; });
    else updatePrefs((p) => ({ ...p, catOrder: next }));
  };
  const moveItem = (gid, id, delta) => {
    const group = groups.find((g) => g.id === gid);
    if (!group) return;
    const next = moveBy(group.items.map((it) => it.id), group.items.findIndex((it) => it.id === id), delta);
    if (layoutEditMode) update((c) => { c.groups.find((g) => g.id === gid).items.sort((a, b) => next.indexOf(a.id) - next.indexOf(b.id)); return c; });
    else updatePrefs((p) => ({ ...p, itemOrder: { ...p.itemOrder, [gid]: next } }));
  };
  const blockDragRef = uR(null);
  const blockOverRef = uR(null);
  const [blockOver, setBlockOver] = uS(null);
  const reorderBlock = (fromId, toId) => {
    update((c) => {
      const arr = (c.layout || []).slice();
      const from = arr.findIndex((b) => b.id === fromId);
      const to = arr.findIndex((b) => b.id === toId);
      if (from > -1 && to > -1 && from !== to) {
        const [moved] = arr.splice(from, 1);
        arr.splice(to, 0, moved);
        c.layout = arr;
      }
      return c;
    });
  };
  const blockDrag = layoutEditMode ? {
    controls(id, title) {
      const index = cfg.layout.findIndex((block) => block.id === id);
      const move = (delta) => update((c) => { c.layout = moveBy(c.layout, index, delta); return c; });
      return <OrderControls label={`模块 ${title}`} onUp={index > 0 ? () => move(-1) : null} onDown={index < cfg.layout.length - 1 ? () => move(1) : null} />;
    },
    overId: blockOver,
    start(e, id) { blockDragRef.current = id; blockOverRef.current = null; setBlockOver(null); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', id); } catch (_) {} },
    enter(id) { if (!blockDragRef.current) return; blockOverRef.current = id; if (blockOver !== id) setBlockOver(id); },
    overBlock(e, id) { if (!blockDragRef.current) return; e.preventDefault(); blockOverRef.current = id; if (blockOver !== id) setBlockOver(id); },
    drop(e, id) { if (!blockDragRef.current) return; e.preventDefault(); if (blockDragRef.current !== id) reorderBlock(blockDragRef.current, id); blockDragRef.current = null; blockOverRef.current = null; setBlockOver(null); },
    end() { if (blockDragRef.current && blockOverRef.current && blockDragRef.current !== blockOverRef.current) reorderBlock(blockDragRef.current, blockOverRef.current); blockDragRef.current = null; blockOverRef.current = null; setBlockOver(null); },
  } : null;

  uE(() => { document.body.style.background = theme(themeModeCurrent).app; document.documentElement.style.colorScheme = dark ? 'dark' : 'light'; }, [themeModeCurrent, dark]);

  const allItems = uM(() => baseGroups.flatMap((g) => g.items.map((it) => ({ ...it, cat: g.name }))), [baseGroups]);
  const byId = uM(() => { const m = {}; allItems.forEach((it) => { m[it.id] = it; }); return m; }, [allItems]);

  const setTheme = () => {
    const next = nextThemeMode(themeMode || cfg.theme);
    try { localStorage.setItem('muyuan.theme', next); } catch {}
    setThemeMode(next);
  };
  const onPin = (id) => {
    if (layoutEditMode) {
      update((c) => {
        const current = Array.isArray(c.pins) ? c.pins : [];
        c.pins = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
        return c;
      });
      return;
    }
    updatePrefs((p) => {
      const current = Array.isArray(p.pins) ? p.pins : cfg.pins;
      p.pins = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      return p;
    });
  };
  const resetPins = () => { if (!layoutEditMode) updatePrefs((p) => { p.pins = null; return p; }); };
  const pinDragRef = uR(null);
  const pinOverRef = uR(null);
  const [pinDragOver, setPinDragOver] = uS(null);
  const reorderPins = (fromId, toId) => {
    if (layoutEditMode) {
      update((c) => {
        const current = Array.isArray(c.pins) ? c.pins : [];
        c.pins = reorderPinnedIds(current, fromId, toId);
        return c;
      });
      return;
    }
    updatePrefs((p) => {
      const current = Array.isArray(p.pins) ? p.pins : pins;
      p.pins = reorderPinnedIds(current, fromId, toId);
      return p;
    });
  };
  const movePin = (id, delta) => {
    if (layoutEditMode) update((c) => { c.pins = moveBy(c.pins, c.pins.indexOf(id), delta); return c; });
    else updatePrefs((p) => {
      const visiblePins = pins.filter((pin) => byId[pin]);
      return { ...p, pins: moveBy(visiblePins, visiblePins.indexOf(id), delta) };
    });
  };
  const pinDrag = editPinsActive ? {
    dragging: pinDragRef.current,
    over: pinDragOver,
    start(e, id) {
      pinDragRef.current = id;
      pinOverRef.current = null;
      setPinDragOver(null);
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', id); } catch (_) {}
    },
    enter(e, id) {
      if (!pinDragRef.current) return;
      e.preventDefault();
      pinOverRef.current = id;
      if (pinDragOver !== id) setPinDragOver(id);
    },
    overCard(e, id) {
      if (!pinDragRef.current) return;
      e.preventDefault();
      pinOverRef.current = id;
      if (pinDragOver !== id) setPinDragOver(id);
    },
    drop(e, id) {
      if (!pinDragRef.current) return;
      e.preventDefault();
      const target = id || pinOverRef.current;
      if (target && pinDragRef.current !== target) reorderPins(pinDragRef.current, target);
      pinDragRef.current = null;
      pinOverRef.current = null;
      setPinDragOver(null);
    },
    end() {
      if (pinDragRef.current && pinOverRef.current && pinDragRef.current !== pinOverRef.current) {
        reorderPins(pinDragRef.current, pinOverRef.current);
      }
      pinDragRef.current = null;
      pinOverRef.current = null;
      setPinDragOver(null);
    },
    mouseStart(e, id) {
      // Some test/browser fallback paths omit `button`; only reject an explicit non-left click.
      if (e.button != null && e.button !== 0) return;
      pinDragRef.current = id;
      pinOverRef.current = null;
      setPinDragOver(null);
    },
    mouseEnter(e, id) {
      if (!pinDragRef.current || e.buttons === 0) return;
      pinOverRef.current = id;
      if (pinDragOver !== id) setPinDragOver(id);
    },
    mouseUp(e, id) {
      if (!pinDragRef.current) return;
      const target = id || pinOverRef.current;
      if (target && pinDragRef.current !== target) reorderPins(pinDragRef.current, target);
      pinDragRef.current = null;
      pinOverRef.current = null;
      setPinDragOver(null);
    },
  } : null;
  const todoDone = (td) => (localDone[td.id] == null ? td.done : localDone[td.id]);
  const toggleTodo = (id) => setLocalDone((prev) => {
    // Front-page todo checks are session-local; admins edit the shared todo definitions in the backend.
    const item = cfg.todos.find((td) => td.id === id);
    return { ...prev, [id]: !(prev[id] == null ? item?.done : prev[id]) };
  });

  const pinnedItems = uM(() => pins.map((id) => byId[id]).filter(Boolean), [pins, byId]);

  const remain = cfg.todos.filter((t) => !todoDone(t)).length;

  // ── global block layout (order + visibility, set in admin) ──
  const lay = uM(() => (Array.isArray(cfg.layout) ? cfg.layout : []), [cfg.layout]);
  const ordOf = (id) => { const i = lay.findIndex((b) => b.id === id); return i < 0 ? 99 : i; };
  const visOf = (id) => { const b = lay.find((x) => x.id === id); return !b || b.visible !== false; };
  const displayOf = (id, visibleDisplay) => (layoutEditMode || visOf(id)) ? visibleDisplay : 'none';
  const navBlockOver = blockDrag && blockDrag.overId === 'nav';

  if (!configReady) {
    return (
      <div style={{
        ...cssVars(false),
        minHeight: '100vh',
        background: 'var(--ws-app)',
        color: 'var(--ws-text3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '"Hanken Grotesk","Noto Sans SC",sans-serif',
        fontSize: 14,
        fontWeight: 700,
      }}>
        <div><p>{configState.error ? '导航加载失败' : '正在加载导航...'}</p>{configState.error && <><p role="alert">{configErrorMessage(configState.error)}</p><button type="button" onClick={() => configState.reload().catch(() => {})}>重试</button></>}</div>
      </div>
    );
  }

  return (
    <div style={{ ...cssVars(themeModeCurrent), minHeight: '100vh', background: 'var(--ws-app)', color: 'var(--ws-text)', fontFamily: '"Hanken Grotesk","Noto Sans SC",sans-serif' }}>
      {layoutEditMode && (
        <div className="ws-layout-editor-toolbar">
          <button type="button" disabled={savingLayout} onClick={onExitLayout}><Icon.close s={15} /><span>退出布局设置</span></button>
          <button type="button" disabled={savingLayout} className="primary" onClick={onSaveLayout}><Icon.check s={15} /><span>{savingLayout ? '保存中...' : '保存布局'}</span></button>
        </div>
      )}
      {/* sticky top bar */}
      <header className="ws-topbar" inert={savingLayout ? '' : undefined} style={{ position: 'sticky', top: 0, zIndex: 20, padding: '14px clamp(20px,4vw,48px)', borderBottom: '1px solid var(--ws-border)', background: 'color-mix(in oklch, var(--ws-app) 88%, transparent)', backdropFilter: 'blur(10px)' }}>
        <div className="ws-brand-slot" style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <Brand size={36} dark={dark} text={site.logoText} img={site.logoImg} />
          <div style={{ lineHeight: 1.15 }}>
            <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>{site.name}</div>
            <div style={{ fontSize: 11, color: 'var(--ws-text3)', fontFamily: '"JetBrains Mono",monospace', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{site.subtitle}</div>
          </div>
        </div>
        <WebSearchBar />
        <div className="ws-actions">
          {w.enabled && (
            <div className={'ws-weather' + (w.loading ? ' loading' : '')}>
              <WeatherIcon weather={w} s={17} />
              {w.loading || w.unavailable ? (
                <span className="ws-weather-status">{w.unavailable ? '天气暂不可用' : '天气更新中'}</span>
              ) : (
                <>
                  <span className="ws-weather-temp">{w.tempNow ?? w.tempHigh}°</span>
                  <span className="ws-weather-meta">{w.cond} · {w.stale ? '缓存天气' : `湿度 ${w.humidity ?? '--'}%`}</span>
                </>
              )}
            </div>
          )}
          <button className="ws-iconbtn" onClick={setTheme} title="切换主题">{dark ? <Icon.sun s={18} /> : <Icon.moon s={18} />}</button>
          {effectiveUser ? (
            <AccountMenu user={effectiveUser} onLogout={doLogout} />
          ) : (
            <button className="ws-loginbtn" onClick={() => setShowLogin(true)} title="员工登录">
              <Icon.user s={16} />
              <span>登录</span>
            </button>
          )}
        </div>
      </header>

      <div inert={savingLayout ? '' : undefined} style={{ maxWidth: 1920, margin: '0 auto', padding: '22px clamp(20px,3.5vw,40px) 52px' }}>
        {!layoutEditMode && configState.error && <div className="app-status error" role="alert">正在显示缓存导航，最新配置暂时无法获取。<button type="button" onClick={() => configState.reload().catch(() => {})}>重试</button></div>}
        {!layoutEditMode && prefsStatus.saving && <p className="app-status" role="status">正在保存个人设置...</p>}
        {!layoutEditMode && prefsStatus.error && <div className="app-status error" role="alert"><span>个人设置尚未同步：{configErrorMessage(prefsStatus.error)}</span><button type="button" onClick={retryPrefs}>{prefsStatus.operation === 'load' ? '重新读取' : '重试同步'}</button></div>}
        <React.Fragment>
            <div className="ws-dash">
              <Widget blockId="clock" blockTitle="时间 · 天气" blockDrag={blockDrag} style={{ order: ordOf('clock'), display: displayOf('clock', 'flex'), flexDirection: 'column', justifyContent: 'space-between', minHeight: 188, background: dark ? 'linear-gradient(150deg, oklch(0.30 0.04 52), var(--ws-card))' : 'linear-gradient(150deg, oklch(0.955 0.03 60), var(--ws-card))' }}>
                <ClockContent site={site} user={effectiveUser} w={w} />
              </Widget>

              <PromotionWidget cfg={cfg} user={effectiveUser} disableEditing={layoutEditMode} blockId="promo" blockTitle="平台大促" blockDrag={blockDrag} style={{ order: ordOf('promo'), display: displayOf('promo', undefined) }} />

              <Widget blockId="announce" blockTitle="公告" blockDrag={blockDrag} style={{ order: ordOf('announce'), display: displayOf('announce', undefined) }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ color: 'var(--ws-accent)' }}><Icon.bell s={16} /></span>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>公告</h3>
                </div>
                <div>
                  {cfg.announcements.map((a, i) => (
                    <div key={a.id} style={{ display: 'flex', gap: 10, padding: '9px 0', borderTop: i ? '1px solid var(--ws-border-soft)' : 'none' }}>
                      <span style={{ flex: '0 0 auto', fontSize: 11, fontWeight: 700, color: 'var(--ws-accent)', background: 'var(--ws-accent-soft)', borderRadius: 5, padding: '2px 7px', height: 'fit-content' }}>{a.tag}</span>
                      <span style={{ fontSize: 13.5, color: 'var(--ws-text2)', lineHeight: 1.45 }}>{a.text}</span>
                    </div>
                  ))}
                  {!cfg.announcements.length && <div style={{ fontSize: 13, color: 'var(--ws-text3)', padding: '8px 0' }}>暂无公告</div>}
                </div>
              </Widget>

              <Widget blockId="todos" blockTitle="今日待办" blockDrag={blockDrag} style={{ order: ordOf('todos'), display: displayOf('todos', undefined) }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>今日待办</h3>
                  <span className="ws-seccount">{remain} 待完成</span>
                </div>
                <div>
                  {cfg.todos.map((td) => {
                    const done = todoDone(td);
                    return (
                    <button key={td.id} aria-pressed={done} onClick={() => toggleTodo(td.id)} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '8px 4px', border: 'none', background: 'transparent', cursor: 'pointer', font: 'inherit', textAlign: 'left', width: '100%' }}>
                      <span style={{ width: 18, height: 18, flex: '0 0 auto', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', border: done ? 'none' : '1.5px solid var(--ws-border)', background: done ? 'var(--ws-accent)' : 'transparent', color: 'var(--ws-accent-text)' }}>{done && <Icon.check s={12} />}</span>
                      <span style={{ flex: 1, fontSize: 13.5, color: done ? 'var(--ws-text3)' : 'var(--ws-text)', textDecoration: done ? 'line-through' : 'none' }}>{td.text}</span>
                      <span style={{ fontSize: 11.5, color: 'var(--ws-text3)', fontFamily: '"JetBrains Mono",monospace' }}>{td.due}</span>
                    </button>
                    );
                  })}
                  {!cfg.todos.length && <div style={{ fontSize: 13, color: 'var(--ws-text3)', padding: '8px 0' }}>暂无待办</div>}
                </div>
              </Widget>
            <div
              className={'ws-bento ws-block-full' + (blockDrag ? ' ws-layout-edit-block' : '') + (blockDrag && blockDrag.overId === 'pins' ? ' over' : '')}
              data-block-id="pins"
              style={{ order: ordOf('pins'), display: displayOf('pins', undefined) }}
              onDragEnter={blockDrag ? () => blockDrag.enter('pins') : undefined}
              onDragOver={blockDrag ? (e) => blockDrag.overBlock(e, 'pins') : undefined}
              onDrop={blockDrag ? (e) => blockDrag.drop(e, 'pins') : undefined}
            >
              {blockDrag && (
                <div className="ws-layout-block-handle" draggable
                  onDragStart={(e) => blockDrag.start(e, 'pins')}
                  onDragEnd={blockDrag.end}
                  title="拖拽排序模块">
                  <Icon.grip s={14} />
                  <span>常用 · 置顶</span>
                  {blockDrag.controls('pins', '常用 · 置顶')}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <span style={{ color: 'var(--ws-accent)' }}><Icon.pin s={15} /></span>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap' }}>常用 · 置顶</h3>
                {editPinsActive && !layoutEditMode && (
                  <button className="ws-resetbtn ws-pin-reset" onClick={resetPins} title="恢复后台设置的常用置顶" style={{ marginLeft: 'auto' }}>
                    <Icon.reset s={14} /><span>恢复默认</span>
                  </button>
                )}
                <div title={editPinsActive ? '编辑中 · 点标签右侧 +/− 增减置顶' : '开启后可点标签右侧 +/− 增减置顶'}
                  style={{ marginLeft: editPinsActive ? 0 : 'auto', display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--ws-border)', background: editPinsActive ? 'var(--ws-accent-soft)' : 'transparent', color: editPinsActive ? 'var(--ws-accent)' : 'var(--ws-text3)', borderRadius: 8, padding: '5px 9px', font: 'inherit', fontSize: 12, fontWeight: 700, transition: 'background .15s, color .15s' }}>
                  <Icon.gear s={14} />
                  <span style={{ whiteSpace: 'nowrap' }}>{editPinsActive ? '编辑中 · 点 +/− 增减' : '编辑置顶'}</span>
                  <button onClick={toggleEdit} aria-pressed={editPinsActive} title={editPinsActive ? '关闭编辑' : '开启编辑'} disabled={layoutEditMode}
                    style={{ width: 30, height: 17, borderRadius: 9, flex: '0 0 auto', padding: 0, border: 'none', cursor: layoutEditMode ? 'default' : 'pointer', background: editPinsActive ? 'var(--ws-accent)' : 'var(--ws-border)', position: 'relative', transition: 'background .15s' }}>
                    <span style={{ position: 'absolute', top: 2, left: editPinsActive ? 15 : 2, width: 13, height: 13, borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 2px oklch(0 0 0 / .28)' }} />
                  </button>
                </div>
              </div>
              {pinnedItems.length ? (
                <div className="ws-pinned-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 11 }}>
                  {pinnedItems.map((it, index) => <PCard key={it.id} item={it} sub={it.note} pinned onPin={onPin} dark={dark} edit={editPinsActive} pinDrag={pinDrag} onMoveUp={index > 0 ? () => movePin(it.id, -1) : null} onMoveDown={index < pinnedItems.length - 1 ? () => movePin(it.id, 1) : null} />)}
                </div>
              ) : (
                <div style={{ padding: '22px 0', textAlign: 'center', color: 'var(--ws-text3)', fontSize: 13.5 }}>还没有置顶 · 把常用入口加到这里</div>
              )}
            </div>

            <div
              className={'ws-block-full' + (blockDrag ? ' ws-layout-edit-block' : '') + (navBlockOver ? ' over' : '')}
              data-block-id="nav"
              style={{ order: ordOf('nav'), display: displayOf('nav', undefined) }}
              onDragEnter={blockDrag ? () => blockDrag.enter('nav') : undefined}
              onDragOver={blockDrag ? (e) => blockDrag.overBlock(e, 'nav') : undefined}
              onDrop={blockDrag ? (e) => blockDrag.drop(e, 'nav') : undefined}
            >
            {blockDrag && (
              <div className="ws-layout-block-handle" draggable
                onDragStart={(e) => blockDrag.start(e, 'nav')}
                onDragEnd={blockDrag.end}
                title="拖拽排序模块">
                <Icon.grip s={14} />
                <span>全部导航</span>
                {blockDrag.controls('nav', '全部导航')}
              </div>
            )}
            <div className="ws-secbar">
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>全部导航</h2>
              <span className="ws-seccount">{groups.length} 个分类</span>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
                {organizeActive && !layoutEditMode && (
                  <button className="ws-resetbtn" onClick={resetOrder} title="恢复默认顺序"><Icon.reset s={14} /><span>恢复默认</span></button>
                )}
                {effectiveUser ? (
                  <div title={organizeActive ? '整理中 · 拖动握柄排序' : '拖拽整理分类与网址顺序'}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--ws-border)', background: organizeActive ? 'var(--ws-accent-soft)' : 'transparent', color: organizeActive ? 'var(--ws-accent)' : 'var(--ws-text3)', borderRadius: 8, padding: '5px 9px', font: 'inherit', fontSize: 12, fontWeight: 700, transition: 'background .15s, color .15s' }}>
                    <Icon.grip s={14} />
                    <span style={{ whiteSpace: 'nowrap' }}>{organizeActive ? '整理中 · 拖动排序' : '整理布局'}</span>
                    <button onClick={() => { if (!layoutEditMode) setOrganize((v) => !v); }} aria-pressed={organizeActive} title={organizeActive ? '完成整理' : '开启整理'} disabled={layoutEditMode}
                      style={{ width: 30, height: 17, borderRadius: 9, flex: '0 0 auto', padding: 0, border: 'none', cursor: layoutEditMode ? 'default' : 'pointer', background: organizeActive ? 'var(--ws-accent)' : 'var(--ws-border)', position: 'relative', transition: 'background .15s' }}>
                      <span style={{ position: 'absolute', top: 2, left: organizeActive ? 15 : 2, width: 13, height: 13, borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 2px oklch(0 0 0 / .28)' }} />
                    </button>
                  </div>
                ) : (
                  <button className="ws-resetbtn" onClick={() => setShowLogin(true)} title="登录后可拖拽排序，顺序仅自己可见"><Icon.user s={14} /><span>登录后可整理顺序</span></button>
                )}
              </div>
            </div>
            {organizeActive && <div className="ws-orgtip"><Icon.grip s={13} /><span>{layoutEditMode ? '后台布局设置中：拖动模块、分类和网址标签可调整全局顺序；网址标签支持跨分类移动，保存后会修改所属分类。' : '拖动分类左上角的握柄可排分类；拖动网址标签可在分类内排序。你的顺序仅存在你的账号下，别人看不到。'}</span></div>}
            <NavigationCatalog groups={groups} pins={pins} onPin={onPin} dark={dark} edit={editPinsActive} organize={organizeActive} drag={drag} moveCategory={moveCategory} moveItem={moveItem} />
            </div>
            </div>
        </React.Fragment>
      </div>
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} onLogin={doLogin} />}
    </div>
  );
}

export default WorkstationApp;
